import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Auth.css';
import Email from '../assets/mail.svg';
import logo from '../assets/logo.png';
import { apiService } from '../services/apiService';
import { useApi } from '../hooks/useApi'; 
import analyticsService from '../services/analyticsService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [userOrg, setUserOrg] = useState(null);

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const data = await apiService.verifySession();
          
          if (!data.code) {
            // const userData = await res.json();
            setCurrentUser(data.user);
            const userRoles = data.user.roles || ['orguser'];
            console.log('🔍 Auth - User data from API:', data.user);
            console.log('🔍 Auth - User roles:', userRoles);
            console.log('🔍 Auth - Profile image URL:', data.user.profileImageUrl);
            console.log('🔍 Auth - Is admin?', userRoles.includes('admin'));
            setUserRoles(userRoles);
            setUserOrg(data.user.organization);
          } else {
            const errorData = data; // await res.json().catch(() => ({}));
            
            // Check if token is blacklisted or invalid
            // if (errorData.code === 'TOKEN_BLACKLISTED' || errorData.code === 'USER_LOGGED_OUT' || res.status === 401) {
              if (errorData.code === 'TOKEN_BLACKLISTED' || errorData.code === 'USER_LOGGED_OUT') {
              console.log('🔒 Token has been invalidated or user logged out, redirecting to login');
              // Clear the invalid token
              localStorage.removeItem('authToken');
              setCurrentUser(null);
              setUserRoles([]);
              setUserOrg(null);
              
              // Redirect to login page if not already there
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              return;
            }
            
            // For other errors, just clear the token
            localStorage.removeItem('authToken');
            setCurrentUser(null);
            setUserRoles([]);
            setUserOrg(null);
          }
        } catch (error) {
          console.error('Session verification error:', error);
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          setUserRoles([]);
          setUserOrg(null);
        }
      }
    };

    checkExistingSession();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('Making login request to apiService.login');
      
      // Clear old state FIRST before logging in to prevent role persistence issues
      console.log('🔄 Clearing old authentication state before login');
      // Clear ALL authentication-related tokens and data
      localStorage.removeItem('authToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('otpToken');
      localStorage.removeItem('userSession');
      localStorage.removeItem('uid');
      localStorage.removeItem('email');
      localStorage.removeItem('orgId');
      localStorage.removeItem('userName');
      localStorage.removeItem('profileImageUrl');
      localStorage.removeItem('userRole');
      localStorage.removeItem('tempRegNumber');
      
      setCurrentUser(null);
      setUserRoles([]);
      setUserOrg(null);
      
      const data = await apiService.login({ email, password });
      
      console.log('Parsed login response data:', data);
      
      // Store the auth token - check multiple possible locations
      const token = data.token || data.accessToken || data.authToken || data.sessionToken;
      if (token) {
        console.log('Storing auth token');
        localStorage.setItem('authToken', token);
      } else {
        console.warn('No token received in response');
        console.log('Available data keys:', Object.keys(data));
      }

      // Set user data from API response - check multiple possible locations
      const userData = data.user || data.userData || data.userInfo || data;
      if (userData && (userData.email || userData.uid || userData.id)) {
        console.log('Setting user data:', userData);
        const roles = userData.roles || ['orguser'];
        console.log('🔍 Setting roles from login response:', roles);
        setCurrentUser(userData);
        setUserRoles(roles);
        setUserOrg(userData.organization || userData.org || userData.organizationId);
      } else {
        console.error('No user data in response');
        console.log('Available data keys:', Object.keys(data));
        throw new Error('Invalid response: missing user data');
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      // Clear state on error to prevent stale data
      localStorage.removeItem('authToken');
      setCurrentUser(null);
      setUserRoles([]);
      setUserOrg(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const res = await apiService.logout();
      console.log('Logout response:', res);
      
      // Check if logout was successful
      if (!res.ok) {
        console.warn('Logout API call failed, but clearing local state anyway');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear ALL authentication-related tokens and data
      console.log('🧹 Clearing all authentication data from localStorage');
      localStorage.removeItem('authToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('otpToken');
      localStorage.removeItem('userSession');
      localStorage.removeItem('uid');
      localStorage.removeItem('email');
      localStorage.removeItem('orgId');
      localStorage.removeItem('userName');
      localStorage.removeItem('profileImageUrl');
      localStorage.removeItem('userRole');
      localStorage.removeItem('tempRegNumber');
      
      setCurrentUser(null);
      setUserRoles([]);
      setUserOrg(null);
    }
  };

  const hasRole = (requiredRole) => {
    return userRoles.includes(requiredRole);
  };

  const hasAnyRole = (requiredRoles) => {
    return requiredRoles.some(role => userRoles.includes(role));
  };

  const value = {
    currentUser,
    userRoles,
    userOrg,
    user: currentUser
      ? { ...currentUser, roles: userRoles, organization: userOrg }
      : null,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, currentUser, userRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getRoles } = useApi();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    console.log('Starting login process for email:', email);
    try {
      const userCredential = await login(email, password);
      console.log('Login successful:', userCredential);
      // Navigation will be handled by the useEffect below
    } catch (err) {
      console.error('Login error in handleSubmit:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      if (err.message.includes('Invalid email or password')) {
        setError('Invalid email or password. Please try again.');
      } else if (err.message.includes('Too many failed login attempts')) {
        setError('Too many failed login attempts. Please try again later.');
      } else {
        setError('Failed to log in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle navigation after successful login
  useEffect(() => {
    const handleLoginRedirect = async () => {
      if (currentUser && location.pathname === '/login' && userRoles.length > 0) {
        // Initialize analytics service with user data
        analyticsService.setUser({
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.name || currentUser.email,
          roles: userRoles,
          organization: currentUser.organization
        });
        
        let redirectPath = '/dashboard'; // Default fallback
        
        try {
          // Fetch roles from database to get landing pages
          const roles = await getRoles();
          console.log('🔍 Auth - Fetched roles:', roles);
          
          // Use the first role's landing page
          const firstRole = userRoles[0];
          const roleData = roles.find(role => role.id === firstRole);
          
          if (roleData && roleData.landingPage) {
            redirectPath = roleData.landingPage;
            console.log('🔍 Auth - Using first role landing page:', firstRole, '->', redirectPath);
          } else {
            console.warn('🔍 Auth - No landing page found for role:', firstRole, ', using default dashboard');
          }
        } catch (error) {
          console.error('🔍 Auth - Error fetching roles:', error);
          console.warn('🔍 Auth - Using default dashboard due to API error');
        }
        
        const from = location.state?.from || redirectPath;
        console.log('🔍 Auth - User roles:', userRoles);
        console.log('🔍 Auth - First role:', userRoles[0]);
        console.log('🔍 Auth - Navigating to:', from);
        navigate(from, { replace: true });
      }
    };

    handleLoginRedirect();
  }, [currentUser, navigate, location, userRoles, getRoles]);

  return (
    <div className="login-page">
      <div className="logo-msg">
        <h1>
          Cars <br />
          <span>in Auction Today</span>
        </h1>
      </div>
      <div className="login-box">
        <div className="Dealer-logo">
          <img src={logo} alt="Trust Auto Logo" />
        </div>
        <div className="welcome-section">
          <h1 className="welcome-title">Back Office</h1>
          <p className="welcome-subtitle">
            Sign in to access Trust Auto RBAC Platform
          </p>
        </div>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@trustauto.in"
              required
              className="form-input"
              autoComplete="username"
              disabled={isLoading}
            />
          </div>
          <div className="input-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
              className="form-input"
              autoComplete="current-password"
              disabled={isLoading}
            />
            <button type="button" className="forgot-link">
              Forgot Password?
            </button>
          </div>
          <div className="form-options">
            <label className="keep-signed">
              <input type="checkbox" name="keepSigned" />
              Keep me signed in
            </label>
          </div>
          {error && <p className="error-message">{error}</p>}
          <button 
            type="submit" 
            className="signin-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'SIGN IN'}
          </button>
        </form>
      </div>
    </div>
  );
};